import React, {useState, useEffect} from "react";
import { Modal, Checkbox, Textarea, TextInput } from '@mantine/core';
import S from './ModalForm.module.scss';
import { useForm } from '@mantine/form';
import {PatternFormat} from "react-number-format";
import {notifications} from "@mantine/notifications";

interface ModalProps {
    opened: boolean;
    close: () => void;
    product: string;
    title: string;
}

interface FormValues {
    name: string;
    company: string;
    phone: string;
    email: string;
    message?: string;
    product: string;
    title: string;
    check: boolean;
}

export const ModalForm = ({
    opened,
    close,
    product,
    title,
}: ModalProps) => {
    const [isLoading, setIsLoading] = useState(false);

    const emailRegex = /^[a-zA-Z0-9._%+-]+@[a-zA-Z0-9.-]+\.[a-zA-Z]{2,}$/;

    const validateForm = (values: FormValues) => {
        const errors: Partial<Record<keyof FormValues, string>> = {};

        if (!values.name) errors.name = 'обязательное поле';
        if (!values.email) {
            errors.email = 'обязательное поле';
        } else if (!emailRegex.test(values.email)) {
            errors.email = 'некорректный email';
        }
        if (!values.phone || values.phone.replace(/\D/g, '').length < 11) {
            errors.phone = 'заполните номер телефона';
        }
        if (!values.check) errors.check = 'необходимо согласие на обработку данных';

        return errors;
    };

    const productForm = useForm<FormValues>({
        initialValues: {
            product: product || '',
            name: '',
            company: '',
            email: '',
            phone: '',
            title: title || '',
            check: false,
        },
        validate: (values) => validateForm(values),
    });

    useEffect(() => {
        if (product || title) {
            productForm.setValues({
                product: product || '',
                title: title || '',
            });
        }
    }, [product, title]);

    const sendRequest = async (values: FormValues) => {
        const formData = new FormData();
        formData.append('name', values.name);
        formData.append('company', values.company || '');
        formData.append('phone', values.phone.replace(/\D/g, ''));
        formData.append('email', values.email);
        formData.append('message', values.message || '');
        formData.append('product', values.product || '');
        formData.append('request', values.title || '');

        const response = await fetch('/api/v1/request', {
            method: 'POST',
            body: formData,
        });

        if (!response.ok) {
            const errorText = await response.text();
            throw new Error(`Ошибка ${response.status}: ${errorText || response.statusText}`);
        }

        return {success: true};
    };

    const handleSubmit = productForm.onSubmit(async (values) => {
        setIsLoading(true);

        try {
            await sendRequest(values);
            notifications.show({
                color: 'green',
                autoClose: 5000,
                title: 'Заявка успешно отправлена!',
                message: '',
            })
            productForm.reset();

        } catch (error) {
            notifications.show({
                color: 'red',
                autoClose: 5000,
                title: 'Произошла ошибка при отправке. Пожалуйста, попробуйте позже.',
                message: '',
            })
        } finally {
            setIsLoading(false);
        }
    });

    const handleClose = () => {
        if (product) {
            productForm.reset();
        }
        if (title) {
            productForm.reset();
        }
        close();
    };

    return (
        <Modal.Root
            opened={opened}
            onClose={handleClose}
            size="xl"
            centered
            zIndex={1002}
        >
            <Modal.Overlay zIndex={1001} />
            <Modal.Content>
                <Modal.Header className={S.header}>
                    <Modal.Title className={S.title}>{title}</Modal.Title>
                    <Modal.CloseButton />
                </Modal.Header>
                <Modal.Body className={S.body}>
                    <form onSubmit={handleSubmit} className={S.boxInput}>
                        <TextInput
                            label="продукт БМК"
                            withAsterisk
                            size="lg"
                            value={product}
                            readOnly
                            error={productForm.errors.product}
                            className={S.textInput}
                        />
                        <div className={S.formRow}>
                            <TextInput
                                label="имя, фамилия"
                                withAsterisk
                                size="lg"
                                key={productForm.key('name')}
                                {...productForm.getInputProps('name')}
                                className={S.textInput}
                                styles={{
                                    error: {
                                        color: '#f85252',
                                        fontSize: '14px',
                                        fontWeight: '400',
                                    }
                                }}
                            />
                            <TextInput
                                label="название организации"
                                size="lg"
                                key={productForm.key('company')}
                                {...productForm.getInputProps('company')}
                                className={S.textInput}
                            />
                        </div>
                        <div className={S.formRow}>
                            <PatternFormat
                                format="+7 ### ### ## ##"
                                mask="_"
                                customInput={TextInput}
                                label="телефон"
                                withAsterisk
                                size="lg"
                                value={productForm.values.phone}
                                {...productForm.getInputProps('phone')}
                                className={S.textInput}
                                styles={{
                                    error: {
                                        color: '#f85252',
                                        fontSize: '14px',
                                        fontWeight: '400',
                                    }
                                }}
                            />
                            <TextInput
                                label="email"
                                withAsterisk
                                size="lg"
                                key={productForm.key('email')}
                                {...productForm.getInputProps('email')}
                                className={S.textInput}
                                styles={{
                                    error: {
                                        color: '#f85252',
                                        fontSize: '14px',
                                        fontWeight: '400',
                                    }
                                }}
                            />
                        </div>
                        <Textarea
                            label="ваше сообщение"
                            autosize
                            minRows={2}
                            maxRows={4}
                            key={productForm.key('message')}
                            {...productForm.getInputProps('message')}
                            className={S.textInput}
                        />
                        <div className={S.text}>*обязательное поле</div>
                        <Checkbox
                            className={product === 'Bench' ? S.checkboxLabelBench : S.checkboxLabel}
                            label={
                                <span className={S.checkboxText}>согласен на обработку <u onClick={(e) => {
                                    e.stopPropagation();
                                    window.open('/agreement', '_blank');
                                }}>персональных данных</u></span>
                            }
                            color="orange"
                            key={productForm.key('check')}
                            {...productForm.getInputProps('check', {type: 'checkbox'})}
                            styles={{
                                error: {
                                    color: '#f85252',
                                    fontSize: '14px',
                                    fontWeight: '400',
                                }
                            }}
                        />
                        <button
                            type="submit"
                            className={product === 'Bench' ? S.submitButtonBench : S.submitButton}
                        >
                            {isLoading ? 'отправка...' : 'отправить'}
                        </button>
                    </form>
                </Modal.Body>
            </Modal.Content>
        </Modal.Root>
    );
};